import React, { useEffect, useState } from "react";
import { IVehicle } from "dataModels/contract";
import { useTranslation } from "react-i18next";
import { IRadioButton } from "@ds/ds-snam/dist/models/radioButton";
import { Row, Col } from "react-grid-system";
import { Input as TextInput, Select, RadiobuttonList, TooltipTrigger, Icon, DatePicker} from "@ds/ds-snam/react";
import { VehicleType, Direction } from "shared/constants";
import { ISelectLabel } from "@ds/ds-snam/dist/models/select";
import _ from "lodash";
import TooltipIcon from "components/TooltipIcon";
import moment from "moment";

interface IVehicleFormDataInput {
    formData: IVehicle,
    onTextInputChange: (inputEvent: Event) => void,
    onManufactoringYearInputChange: (inputEvent: Event) => void,
    onRadioChange: (inputEvent: Event, fieldName: string) => void,
    onLoadingSideChange: (inputEvent: Event) => void,
    //onDateInputChange: (inputEvent: Event, fieldName: string) => void,
}
const VehicleFormDataInput: React.FC<IVehicleFormDataInput> = (props): JSX.Element => {
    const { formData, onTextInputChange,onManufactoringYearInputChange, onRadioChange, onLoadingSideChange } = props;
    const { t, i18n } = useTranslation(['contract', 'app']);
    const [radioOption, setRadioOption] = useState<IRadioButton[] | undefined>([
        { "id": "0", "label": t('general.noLabel', { ns: 'app' }), "value": "0" },
        { "id": "1", "label": t('general.yesLabel', { ns: 'app' }), "value": "1" }
    ]);
    const [loadingSideOptions, setLoadingSideOptions] = useState<ISelectLabel[] | undefined>();

    useEffect(() => {
        //Radio
        /*setRadioOption([
            { "id": "0", "label": t('general.noLabel', { ns: 'app' }), "value": "0" },
            { "id": "1", "label": t('general.yesLabel', { ns: 'app' }), "value": "1" }
        ]);*/
        //Loading Side Select options
        let options: ISelectLabel[] = [];
        _.forIn(Direction, (value: number, key: string) => {
            if (isNaN(Number(key))) {
                //console.log(value, key)
                //In case of edit, it needs to be preselected with actual form data, false otherwise
                const isSelected: boolean = value === formData.loadingSide;
                let label: string = t('vehicle.vehicleData.loadingDirections.' + value.toString());
                label = label.charAt(0).toUpperCase() + label.slice(1);
                options.push({ id: value.toString(), label: label, value: value.toString(), selected: isSelected });
            }
        })
        setLoadingSideOptions(options);

    }, [i18n.language, t, formData.loadingSide]);

    return <>
        {formData.type !== VehicleType.ISO_CONTAINER && <Row>
            {/**Targa */}
            <Col xs={12} md={4}>
                <div className="textInputWrapper">
                    <TextInput
                        inputLabel={t('vehicle.vehicleForm.sections.dataInput.fields.0', { ns: 'contract' }) + " *"}
                        inputValue={formData.licencePlate}
                        inputName="licencePlate"
                        inputId="licencePlate"
                        inputDescription=""
                        inputStatus="default"
                        inputPlaceholder={t('vehicle.vehicleForm.sections.dataInput.fields.0', { ns: 'contract' }) + " *"}
                        isDisabled={false}
                        input-event-type="single_character"
                        isNumberVariant={false}
                        isReadonly={false}
                        onInputChange={onTextInputChange}
                    />
                </div>
            </Col>
        </Row>}

        {formData.type === VehicleType.ISO_CONTAINER && <Row>
            {/**Iso container code */}
            <Col xs={12} md={4}>
                <div className="textInputWrapper">
                    <TextInput
                        inputLabel={t('vehicle.vehicleForm.sections.dataInput.fields.1', { ns: 'contract' }) + " *"}
                        inputValue={formData.isoContainerCode}
                        inputName="isoContainerCode"
                        inputId="isoContainerCode"
                        inputDescription=""
                        inputStatus="default"
                        inputPlaceholder={t('vehicle.vehicleForm.sections.dataInput.fields.1', { ns: 'contract' }) + " *"}
                        isDisabled={false}
                        input-event-type="single_character"
                        isNumberVariant={false}
                        isReadonly={false}
                        onInputChange={onTextInputChange}
                    />
                </div>
            </Col>
        </Row>}

        <Row>
            {/** Peso */}
            <Col xs={12} md={2}>
                <div className="textInputWrapper">
                    <TextInput
                        inputLabel={t('vehicle.vehicleForm.sections.dataInput.fields.2', { ns: 'contract' }) + " *"}
                        inputValue={formData.emptyWeight.toString()}
                        inputName="emptyWeight"
                        inputId="emptyWeight"
                        inputDescription=""
                        inputStatus="default"
                        inputPlaceholder={t('vehicle.vehicleForm.sections.dataInput.fields.2', { ns: 'contract' }) + " *"}
                        isDisabled={false}
                        input-event-type="single_character"
                        isNumberVariant={true}
                        minValue={0}
                        isReadonly={false}
                        onInputChange={onTextInputChange}
                    />
                    <div className="textInputWrapperDesc">{t('vehicle.vehicleForm.sections.dataInput.fieldDesc.0')}</div>
                </div>
            </Col>

            {/** Volume + Pressione di progetto */}
            {(formData.type === VehicleType.TANKER || formData.type === VehicleType.ISO_CONTAINER) &&
                <>
                    <Col xs={12} md={2}>
                        <div className="textInputWrapper">
                            <TextInput
                                inputLabel={t('vehicle.vehicleForm.sections.dataInput.fields.3', { ns: 'contract' }) + " *"}
                                inputValue={formData.volume.toString()}
                                inputName="volume"
                                inputId="volume"
                                inputDescription=""
                                inputStatus="default"
                                inputPlaceholder={t('vehicle.vehicleForm.sections.dataInput.fields.3', { ns: 'contract' }) + " *"}
                                isDisabled={false}
                                input-event-type="single_character"
                                isNumberVariant={true}
                                minValue={0}
                                isReadonly={false}
                                onInputChange={onTextInputChange}
                            />
                            <div className="textInputWrapperDesc">{t('vehicle.vehicleForm.sections.dataInput.fieldDesc.1')}</div>
                        </div>
                    </Col>

                    <Col xs={12} md={2}>
                        <div className="textInputWrapper">
                            <TextInput
                                inputLabel={t('vehicle.vehicleForm.sections.dataInput.fields.4', { ns: 'contract' }) + " *"} // Pressione
                                inputValue={formData.designPressure.toString()}
                                inputName="designPressure"
                                inputId="designPressure"
                                inputDescription=""
                                inputStatus="default"
                                inputPlaceholder={t('vehicle.vehicleForm.sections.dataInput.fields.4', { ns: 'contract' }) + " *"} 
                                isDisabled={false}
                                minValue={0}
                                input-event-type="single_character"
                                isNumberVariant={true}
                                isReadonly={false}
                                onInputChange={onTextInputChange}
                            />
                            <div className="textInputWrapperDesc">{t('vehicle.vehicleForm.sections.dataInput.fieldDesc.2')}</div>
                        </div>
                        
                    </Col></>}
        </Row>

        {formData.type === VehicleType.ISO_CONTAINER && <Row>
            {/**Tempo di tenuta */}
            <Col xs={12} md={4}>
                <div className="textInputWrapper">
                    <TextInput
                        inputLabel={t('vehicle.vehicleForm.sections.dataInput.fields.5', { ns: 'contract' }) + " *"}
                        inputValue={formData.holdingTime.toString()}
                        inputName="holdingTime"
                        inputId="holdingTime"
                        inputDescription=""
                        inputStatus="default"
                        inputPlaceholder={t('vehicle.vehicleForm.sections.dataInput.fields.5', { ns: 'contract' }) + " *"}
                        isDisabled={false}
                        input-event-type="single_character"
                        isNumberVariant={true}
                        minValue={0}
                        isReadonly={false}
                        onInputChange={onTextInputChange}
                    />
                    <div className="textInputWrapperDesc">{t('vehicle.vehicleForm.sections.dataInput.fieldDesc.3')}</div>
                </div>
            </Col>
        </Row>}

        {/**Max livello riempimento e max peso */}
        {(formData.type === VehicleType.ISO_CONTAINER || formData.type === VehicleType.TANKER) && <Row>
            <Col xs={12} md={4}>
                <div className="textInputWrapper">
                    <TextInput
                        inputLabel={t('vehicle.vehicleForm.sections.dataInput.fields.6', { ns: 'contract' })}
                        inputValue={formData.maxFillingDegree.toString()}
                        inputName="maxFillingDegree"
                        inputId="maxFillingDegree"
                        inputDescription=""
                        inputStatus="default"
                        inputPlaceholder={t('vehicle.vehicleForm.sections.dataInput.fields.6', { ns: 'contract' })}
                        isDisabled={false}
                        input-event-type="single_character"
                        isNumberVariant={true}
                        minValue={0}
                        isReadonly={false}
                        onInputChange={onTextInputChange}
                    />
                    <div className="textInputWrapperDesc">{t('vehicle.vehicleForm.sections.dataInput.fieldDesc.4')}</div>
                </div>

            </Col>
            {/*<Col xs={12} md={4}>
                <div className="textInputWrapper">
                    <TextInput
                        inputLabel={t('vehicle.vehicleForm.sections.dataInput.fields.7', { ns: 'contract' })}
                        inputValue={formData.maxFillingWeight.toString()}
                        inputName="maxFillingWeight"
                        inputId="maxFillingWeight"
                        inputDescription=""
                        inputStatus="default"
                        inputPlaceholder=""
                        isDisabled={false}
                        input-event-type="single_character"
                        isNumberVariant={true}
                        isReadonly={false}
                        onInputChange={onTextInputChange}
                    />
                    <div className="textInputWrapperDesc">{t('vehicle.vehicleForm.sections.dataInput.fieldDesc.0')}</div>
                </div>

            </Col>*/}
        </Row>}

        {/** Conferma di isolamento termico a vuoto */}
        {(formData.type === VehicleType.ISO_CONTAINER || formData.type === VehicleType.TANKER) &&
            <Row>
                <Col>
                    <div className="radioButtonGroupWrapper">
                        <label >
                            {t('vehicle.vehicleForm.sections.dataInput.fields.8', { ns: 'contract' })+" *"}
                        </label>
                        <RadiobuttonList
                            radioListData={radioOption ? radioOption : []}
                            radioListName="Radio List"
                            radioListDisabled={false}
                            selectedRadioID={formData.vacuumIsolation}
                            radioListOrientation="horizontal"
                            onRadioChange={(event: Event) => {
                                onRadioChange(event, "vacuumIsolation")
                            }}
                        />
                    </div>
                </Col>
            </Row>}

        {/** Produttore + anno fabbbricazione */}
        {(formData.type === VehicleType.ISO_CONTAINER || formData.type === VehicleType.TANKER) &&
            <Row>
                <Col xs={12} md={4}>
                    <div className="textInputWrapper">
                        <TextInput
                            inputLabel={t('vehicle.vehicleForm.sections.dataInput.fields.9', { ns: 'contract' }) + " *"}
                            inputValue={formData.manufacturer}
                            inputName="manufacturer"
                            inputId="manufacturer"
                            inputDescription=""
                            inputStatus="default"
                            inputPlaceholder={t('vehicle.vehicleForm.sections.dataInput.fields.9', { ns: 'contract' }) + " *"}
                            isDisabled={false}
                            input-event-type="single_character"
                            isNumberVariant={false}
                            isReadonly={false}
                            onInputChange={onTextInputChange}
                        />
                    </div>
                </Col>
                <Col xs={12} md={3}>
                    <div className="textInputWrapper">
                        {/*<TextInput
                            inputLabel={t('vehicle.vehicleForm.sections.dataInput.fields.10', { ns: 'contract' }) + " *"}
                            inputValue={formData.yearManufacture}
                            inputName="yearManufacture"
                            inputId="yearManufacture"
                            inputDescription=""
                            inputStatus="default"
                            inputPlaceholder={t('vehicle.vehicleForm.sections.dataInput.fields.10', { ns: 'contract' }) + " *"}
                            isDisabled={false}
                            input-event-type="single_character"
                            isNumberVariant={false}
                            isReadonly={false}
                            onInputChange={onTextInputChange}
                        />*/}
                        <DatePicker
                            id={'date-picker-id'}
                            datePickerTitle={t('vehicle.vehicleForm.sections.dataInput.fields.10', { ns: 'contract' }) + " *"}
                            isRangePicker={false}
                            datePickerVariant="years-only"
                            startingYear={1950}
                            endingYear={new Date().getFullYear()}
                            defaultYear={new Date().getFullYear()}
                            isDisabled={false}
                            selectedValue={formData.yearManufacture ? moment(formData.yearManufacture).toDate() : undefined}
                            datePickerStatus="default"
                            confirmButtonLabel={t('general.buttons.next', { ns: 'app' })}
                            hasCloseButton={false}
                            zIndex={999}
                            isReadonly={false}
                            onDateSelection={(event: Event) => {
                                onManufactoringYearInputChange(event);
                            }}
                            onResetCalendar={()=>{
                                //todo
                            }}
                        ></DatePicker> 
                    </div>
                </Col>
            </Row>
        }

        {/**Indicatori di livello doppi*/}
        {(formData.type === VehicleType.TANKER || formData.type === VehicleType.ISO_CONTAINER) &&
            <Row>
                <Col>
                    <div className="radioButtonGroupWrapper">
                        <label >
                            {t('vehicle.vehicleForm.sections.dataInput.fields.11', { ns: 'contract' })+" *"}
                        </label>
                        <RadiobuttonList
                            radioListData={radioOption ? radioOption : []}
                            radioListName="Level indicator List"
                            radioListDisabled={false}
                            selectedRadioID={formData.twoLevelsIndicators}
                            radioListOrientation="horizontal"
                            onRadioChange={(event: Event) => {
                                onRadioChange(event, "twoLevelsIndicators")
                            }}
                        />
                    </div>
                </Col>
            </Row>
        }

        {/** Connessioni di terra */}
        {/*<Row>
            <Col xs={12} md={4}>
                <div className="radioButtonGroupWrapper">
                    <label >
                        {t('vehicle.vehicleForm.sections.dataInput.fields.12', { ns: 'contract' })+" *"}
                    </label>
                    <RadiobuttonList
                        radioListData={radioOption ? radioOption : []}
                        radioListName="grounding connection List"
                        radioListDisabled={false}
                        selectedRadioID={formData.groundingConnection}
                        radioListOrientation="horizontal"
                        onRadioChange={(event: Event) => {
                            onRadioChange(event, "groundingConnection")
                        }}
                    />
                </div>
            </Col>
        </Row>*/}

        {/**Presenza rompifiamma */}
        {formData.type === VehicleType.TRUCK &&
            <Row>
                <Col>
                    <div className="radioButtonGroupWrapper">
                        <div>
                        <div style={{float:'left',paddingTop:'1px'}}><label >
                            {t('vehicle.vehicleForm.sections.dataInput.fields.13', { ns: 'contract' })}
                        </label></div>
                        <div style={{ display: "flex", alignItems: "flex-start", flexDirection:"column", gap: "4px",float:'left',marginLeft:'5px' }}>
                            <TooltipIcon tooltipId={"_Tooltip_id"} tooltipData={t('vehicle.vehicleForm.sections.dataInput.fields.15', { ns: 'contract' })}/>
                        </div>
                        </div>
                        <RadiobuttonList
                            radioListData={radioOption ? radioOption : []}
                            radioListName="flame Arrest"
                            radioListDisabled={false}
                            selectedRadioID={formData.flameArrest}
                            radioListOrientation="horizontal"
                            onRadioChange={(event: Event) => {
                                onRadioChange(event, "flameArrest")
                            }}
                        />
                    </div>
                </Col>
            </Row>
        }

        {/** Lato caricamento */}
        {(formData.type === VehicleType.ISO_CONTAINER || formData.type === VehicleType.TANKER) &&
            <Row>
                <Col xs={12} md={4}>
                    <div className="textInputWrapper">
                        <Select
                            id="loadingSideSelector"
                            menuOptions={loadingSideOptions ? loadingSideOptions : []}
                            menuLabel={t('vehicle.vehicleForm.sections.dataInput.fields.14') +" *"}
                            menuId="select-id"
                            isDisabled={false}
                            selectStatus="default"
                            isPanelTop={true}
                            onOptionSelection={onLoadingSideChange}
                        ></Select>
                    </div>
                </Col>
                <Col xs={12} md={4}></Col>
            </Row>

        }

    </>
}

export default VehicleFormDataInput